import type { FC, ReactNode } from "react";
import { createContext, useContext, useState } from "react";

// Define the shape of UTM parameters
export interface UTMParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_theme?: string;
}

// Define the shape of the context data
interface UTMContextType {
  utmTags: UTMParams | undefined;
  initializeUtmTags: () => void;
  clearUtmTags: () => void;
}

// Create the context
const UTMContext = createContext<UTMContextType | undefined>(undefined);

// Provider component
export const UTMProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [utmTags, setUtmTags] = useState<UTMParams | undefined>(() => {
    if (typeof window !== "undefined") {
      const storedUtmTags = localStorage.getItem("utmTags");
      return storedUtmTags ? JSON.parse(storedUtmTags) : undefined;
    }
    return undefined;
  });

  const initializeUtmTags = () => {
    if (typeof window === "undefined") return;

    const queryParams = new URLSearchParams(window.location.search);
    const utmParams: UTMParams = {
      utm_source: queryParams.get("utm_source") || undefined,
      utm_medium: queryParams.get("utm_medium") || undefined,
      utm_campaign: queryParams.get("utm_campaign") || undefined,
      utm_content: queryParams.get("utm_content") || undefined,
      utm_theme: queryParams.get("utm_theme") || undefined,
    };

    // Check if all UTM fields are undefined
    const allFieldsUndefined = Object.values(utmParams).every((value) => value === undefined);
    const serializedUtmParams = JSON.stringify(utmParams);
    const serializedStateUtmTags = JSON.stringify(utmTags);
    const serializedStoredUtmTags = localStorage.getItem("utmTags");

    if (allFieldsUndefined) {
      if (utmTags !== undefined) {
        setUtmTags(undefined); // Only update state if it is different
      }

      if (serializedStoredUtmTags !== null) {
        localStorage.removeItem("utmTags"); // Keep storage in sync when URL has no UTM params
      }
    } else {
      if (serializedStateUtmTags !== serializedUtmParams) {
        setUtmTags(utmParams); // Only update state if it is different
      }

      if (serializedStoredUtmTags !== serializedUtmParams) {
        localStorage.setItem("utmTags", JSON.stringify(utmParams)); // Save to local storage
      }
    }
  };

  const clearUtmTags = () => {
    setUtmTags(undefined);
    if (typeof window !== "undefined") {
      localStorage.removeItem("utmTags");
    }
  };

  return (
    <UTMContext.Provider value={{ utmTags, initializeUtmTags, clearUtmTags }}>
      {children}
    </UTMContext.Provider>
  );
};

// Hook to use the UTM context
export const useUTMContext = () => {
  const context = useContext(UTMContext);
  if (!context) {
    throw new Error("useUTMContext must be used within a UTMProvider");
  }
  return context;
};
